import { formatINR, computeBreakdown, resolveTiersV2 } from "@/lib/quotationApi";
import { Share2, Download, Percent } from "lucide-react";
import { useBranding } from "@/lib/branding";
import PaymentPlanBlock from "@/components/PaymentPlanBlock";
import SummaryTierTotals from "@/components/steps/output/SummaryTierTotals";
import SummaryBreakdownTable from "@/components/steps/output/SummaryBreakdownTable";
import SummaryRooms from "@/components/steps/output/SummaryRooms";
import SummaryShareCards from "@/components/steps/output/SummaryShareCards";

export default function Step5Output({
  data,
  savedId,
  savedStatus = "draft",
  canApprove = false,
  onApprove,
  onUnapprove,
  approving = false,
  showApprovalTrail = false,
  approvedByName = null,
  approvedAt = null,
  activity = [],
  onSave,
  onShareWhatsApp,
  onDownloadPDF,
  onChange,
  saving,
  downloading,
  readOnly,
  editing,
  dirty,
  tierConfigV2,
  selectedTierIds,
  tiersSnapshot,
  paymentPlans = [],
  paymentPlan,
}) {
  const b = computeBreakdown(data);
  const tieredV2 = resolveTiersV2(data, tiersSnapshot, tierConfigV2, selectedTierIds);
  const tierList = tieredV2.list || [];
  // Display order: highest grand total → lowest. Keep the original `tierList`
  // for finish-cascade lookups (`resolveFinishRateV2` walks configured order).
  const sortedTierList = [...tierList].sort(
    (a, z) => (z.breakdown?.grand_total || 0) - (a.breakdown?.grand_total || 0),
  );
  const validAccessories = (data.accessories || []).filter((a) => (a.quantity || 0) > 0);
  const customerPlace = [data.customer.location, data.customer.city].filter(Boolean).join(", ");
  const { branding } = useBranding();
  const validityDays = Math.max(1, parseInt(branding.validity_days, 10) || 15);
  const footerNote = (branding.footer_note || "").trim();
  const resolvedPaymentPlan =
    paymentPlan ||
    (data?.payment_plan_id
      ? (paymentPlans || []).find((p) => p.id === data.payment_plan_id)
      : null);

  return (
    <div data-testid="step-5-output">
      <div className="mb-6">
        <div className="overline text-[#C5A880] mb-2">Step 06 · Summary</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold leading-tight">
          Final quotation
        </h1>
        <p className="text-white/50 mt-2 text-sm">
          Review, adjust discount/tax, save and share.
        </p>
      </div>

      <SummaryTierTotals tierList={tierList} sortedTierList={sortedTierList} data={data} savedId={savedId} />

      <SummaryBreakdownTable tierList={sortedTierList} breakdown={b} />

      {/* Discount & Tax inputs */}
      {!readOnly && (
        <div className="card-luxe mb-4">
          <div className="overline text-white/50 mb-3">Discount & Tax</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-luxe">Discount (₹)</label>
              <input
                type="number"
                inputMode="numeric"
                className="input-luxe"
                placeholder="0"
                value={data.discount_amount || ""}
                onChange={(e) =>
                  onChange({ ...data, discount_amount: e.target.value })
                }
                data-testid="discount-input"
              />
            </div>
            <div>
              <label className="label-luxe flex items-center gap-1.5">
                <Percent size={11} className="text-[#C5A880]/80" />
                Tax %
              </label>
              <input
                type="number"
                inputMode="decimal"
                className="input-luxe"
                placeholder="0"
                value={data.tax_percent || ""}
                onChange={(e) =>
                  onChange({ ...data, tax_percent: e.target.value })
                }
                data-testid="tax-input"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            {[5, 12, 18, 28].map((t) => (
              <button
                key={t}
                onClick={() => onChange({ ...data, tax_percent: t })}
                className={`text-[11px] px-2.5 py-1 rounded-md border transition ${
                  Number(data.tax_percent) === t
                    ? "border-[#C5A880]/60 bg-[#C5A880]/10 text-[#C5A880]"
                    : "border-white/10 text-white/50 hover:border-white/25"
                }`}
                data-testid={`tax-preset-${t}`}
              >
                {t}%
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Customer */}
      <div className="card-luxe mb-4">
        <div className="overline text-white/50 mb-3">Customer</div>
        <div className="space-y-1.5 text-sm">
          <Row label="Name" value={data.customer.name} />
          <Row label="Mobile" value={data.customer.mobile} />
          <Row
            label="City"
            value={customerPlace}
          />
          <Row label="Given by" value={data.customer.quotation_given_by} />
        </div>
      </div>

      <SummaryRooms data={data} tierList={sortedTierList} />

      {/* Accessories */}
      {validAccessories.length > 0 && (
        <div className="card-luxe mb-4">
          <div className="overline text-white/50 mb-3">Accessories</div>
          <div className="space-y-2">
            {validAccessories.map((a) => (
              <div
                key={a.id}
                className="flex justify-between items-center text-sm py-1.5 border-b border-white/5 last:border-0"
                data-testid={`summary-accessory-${a.id}`}
              >
                <div>
                  <div className="font-medium">{a.name}</div>
                  <div className="text-xs text-white/40">
                    {formatINR(a.price_per_unit)} × {a.quantity}
                  </div>
                </div>
                <div className="font-display font-semibold text-[#C5A880]">
                  {formatINR(a.price_per_unit * a.quantity)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.remarks && (
        <div className="card-luxe mb-4">
          <div className="overline text-white/50 mb-2">Remarks</div>
          <p className="text-sm text-white/80 whitespace-pre-wrap">{data.remarks}</p>
        </div>
      )}

      {/* Validity notice */}
      <div
        className="rounded-xl border border-[#C5A880]/30 bg-[#C5A880]/[0.06] px-4 py-3 mb-4 text-center"
        data-testid="validity-note"
      >
        <div className="overline text-[#C5A880] text-[10px] mb-1">Validity</div>
        <p className="text-sm text-white/80">
          This quotation is{" "}
          <span className="text-[#C5A880] font-semibold">
            valid for {validityDays} {validityDays === 1 ? "day" : "days"} only
          </span>{" "}
          from the date of issue.
        </p>
      </div>

      {/* Payment plan */}
      <PaymentPlanBlock
        plan={resolvedPaymentPlan}
        testId="summary-payment-plan"
        primary={branding.primary_color || "#C5A880"}
      />

      {/* Admin-configurable footer note */}
      {footerNote && (
        <div
          className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 mb-4"
          data-testid="footer-note"
        >
          <div className="overline text-white/40 text-[10px] mb-1">Note</div>
          <p className="text-sm text-white/70 whitespace-pre-wrap leading-snug">
            {footerNote}
          </p>
        </div>
      )}

      <SummaryShareCards
        savedId={savedId}
        savedStatus={savedStatus}
        canApprove={canApprove}
        onApprove={onApprove}
        onUnapprove={onUnapprove}
        approving={approving}
        showApprovalTrail={showApprovalTrail}
        approvedByName={approvedByName}
        approvedAt={approvedAt}
        activity={activity}
        readOnly={readOnly}
      />

      {/* Actions */}
      {!readOnly && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              type="button"
              className="btn-ghost flex items-center justify-center gap-2 disabled:opacity-60"
              onClick={onDownloadPDF}
              disabled={downloading}
              data-testid="pdf-btn"
            >
              <Download size={16} />
              {downloading ? "Generating…" : "Download PDF"}
            </button>
            <button
              type="button"
              className="btn-ghost flex items-center justify-center gap-2"
              onClick={onShareWhatsApp}
              data-testid="whatsapp-btn"
            >
              <Share2 size={16} />
              WhatsApp
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, testId, accent }) {
  return (
    <div className="flex justify-between gap-4" data-testid={testId}>
      <span className="text-white/50">{label}</span>
      <span className={accent === "red" ? "text-red-400 text-right" : "text-white/90 text-right"}>
        {value || "—"}
      </span>
    </div>
  );
}
